import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '@/components/base/Button';
import { Field, CheckboxField, Honeypot, FormStatus } from '@/components/base/FormFields';
import { useFormSubmit } from '@/hooks/useFormSubmit';
import { OTHER_CATEGORY_VALUE } from '@/data/site';
import {
  SURVEY_CATEGORY_OPTIONS,
  SURVEY_SINGLE_QUESTIONS,
  SURVEY_CORE_TOTAL,
  COMMISSION_QUESTION,
  COMMISSION_TRIGGER_VALUES,
  Q1_KEY,
  Q1_PROMPT,
  Q7_KEY,
} from '@/data/survey';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db73i0gd6mcdj80oo78g';
const FORM_ID = 'supplier_insights';
const RESULT_KEY = 'kor38_supplier_insights';
const THANK_YOU_PATH = '/supplier-insights/thank-you';

type Step = {
  key: string;
  prompt: string;
  number: number | null;
  kind: 'single' | 'multi' | 'contact';
  optional?: boolean;
  options?: string[];
};

type OptionButtonProps = {
  label: string;
  selected: boolean;
  multi?: boolean;
  onClick: () => void;
};

function OptionButton({ label, selected, multi, onClick }: OptionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`flex w-full items-center gap-3 rounded-md border px-4 py-4 text-left transition-colors duration-200 cursor-pointer ${
        selected
          ? 'border-primary-500 bg-primary-500/10'
          : 'border-background-300 bg-background-100 hover:border-primary-500/60'
      }`}
    >
      <span
        className={`flex h-5 w-5 shrink-0 items-center justify-center border ${
          multi ? 'rounded-[4px]' : 'rounded-full'
        } ${selected ? 'border-primary-400 bg-primary-500' : 'border-background-400'}`}
      >
        {selected ? <i className="ri-check-line text-xs text-background-50" aria-hidden="true"></i> : null}
      </span>
      <span className="text-[15px] leading-relaxed text-foreground-800">{label}</span>
    </button>
  );
}

export default function SupplierSurvey() {
  const navigate = useNavigate();
  const { status, submitting, errorMessage, handleSubmit } = useFormSubmit();
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [categories, setCategories] = useState<string[]>([]);
  const [otherText, setOtherText] = useState('');
  const [currentKey, setCurrentKey] = useState<string>(Q1_KEY);
  const [marketingConsent, setMarketingConsent] = useState(false);

  const showCommission = COMMISSION_TRIGGER_VALUES.includes(answers[Q7_KEY] || '');

  const steps = useMemo<Step[]>(() => {
    const list: Step[] = [{ key: Q1_KEY, prompt: Q1_PROMPT, number: 1, kind: 'multi' }];
    SURVEY_SINGLE_QUESTIONS.forEach((question) => {
      list.push({
        key: question.key,
        prompt: question.prompt,
        number: question.number,
        kind: 'single',
        options: question.options,
      });
      if (question.key === Q7_KEY && showCommission) {
        list.push({
          key: COMMISSION_QUESTION.key,
          prompt: COMMISSION_QUESTION.prompt,
          number: null,
          kind: 'single',
          optional: true,
          options: COMMISSION_QUESTION.options,
        });
      }
    });
    list.push({ key: 'contact', prompt: 'Your details', number: null, kind: 'contact' });
    return list;
  }, [showCommission]);

  useEffect(() => {
    if (status === 'success') {
      try {
        sessionStorage.setItem(
          RESULT_KEY,
          JSON.stringify({ marketingOptIn: marketingConsent, ts: Date.now() }),
        );
      } catch {
        /* storage unavailable — thank-you page falls back to a neutral state */
      }
      navigate(THANK_YOU_PATH);
    }
  }, [status, marketingConsent, navigate]);

  const stepIndex = Math.max(0, steps.findIndex((s) => s.key === currentKey));
  const step = steps[stepIndex] ?? steps[0];

  const toggleCategory = (category: string) => {
    setCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category],
    );
  };

  const selectSingle = (key: string, value: string) => {
    setAnswers((prev) => {
      const next = { ...prev, [key]: value };
      if (key === Q7_KEY && !COMMISSION_TRIGGER_VALUES.includes(value)) {
        delete next[COMMISSION_QUESTION.key];
      }
      return next;
    });
  };

  const isAnswered = () => {
    if (step.optional || step.kind === 'contact') return true;
    if (step.kind === 'multi') {
      if (categories.length === 0) return false;
      if (categories.includes(OTHER_CATEGORY_VALUE) && !otherText.trim()) return false;
      return true;
    }
    return Boolean(answers[step.key]);
  };

  const goNext = () => {
    const next = steps[stepIndex + 1];
    if (next) setCurrentKey(next.key);
  };

  const goBack = () => {
    const prev = steps[stepIndex - 1];
    if (prev) setCurrentKey(prev.key);
  };

  const progress =
    step.kind === 'contact'
      ? 100
      : step.number
        ? (step.number / SURVEY_CORE_TOTAL) * 100
        : (7 / SURVEY_CORE_TOTAL) * 100;

  const counter =
    step.kind === 'contact'
      ? 'Final step \u2014 your details'
      : step.optional
        ? 'Optional follow-up question'
        : `Question ${step.number} of ${SURVEY_CORE_TOTAL}`;

  const otherSelected = categories.includes(OTHER_CATEGORY_VALUE);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (submitting) {
      event.preventDefault();
      return;
    }
    const stamp = event.currentTarget.elements.namedItem('submitted_at');
    if (stamp instanceof HTMLInputElement) {
      stamp.value = new Date().toISOString();
    }
    void handleSubmit(event, SUBMIT_ADDR);
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-8">
      {/* Progress */}
      <div className="mb-7">
        <div className="flex items-center justify-between gap-4">
          <span className="font-label text-[11px] uppercase tracking-[0.16em] text-primary-400">
            {counter}
          </span>
          <span className="font-label text-[11px] uppercase tracking-[0.16em] text-foreground-600">
            {Math.round(progress)}%
          </span>
        </div>
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-background-300">
          <div
            className="h-full rounded-full bg-primary-500 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div aria-live="polite">
        {step.kind === 'multi' ? (
          <fieldset>
            <legend className="font-heading text-xl md:text-2xl leading-snug text-foreground-950">
              {step.prompt}
            </legend>
            <p className="mt-2 text-sm text-foreground-600">Select all that apply.</p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SURVEY_CATEGORY_OPTIONS.map((category) => (
                <OptionButton
                  key={category}
                  label={category}
                  multi
                  selected={categories.includes(category)}
                  onClick={() => toggleCategory(category)}
                />
              ))}
            </div>
            {otherSelected ? (
              <input
                type="text"
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                placeholder="Please specify the category"
                aria-label="Please specify the category"
                className="mt-4 w-full rounded-md border border-background-300 bg-background-100 px-4 py-3 text-base text-foreground-950 placeholder:text-foreground-600 transition-colors duration-200 focus:border-primary-500"
              />
            ) : null}
          </fieldset>
        ) : null}

        {step.kind === 'single' ? (
          <fieldset>
            <legend className="font-heading text-xl md:text-2xl leading-snug text-foreground-950">
              {step.prompt}
            </legend>
            {step.optional ? (
              <p className="mt-2 text-sm text-foreground-600">
                Optional. This does not indicate any commitment to pay KOR38.
              </p>
            ) : null}
            <div className="mt-5 grid grid-cols-1 gap-3">
              {(step.options || []).map((option) => (
                <OptionButton
                  key={option}
                  label={option}
                  selected={answers[step.key] === option}
                  onClick={() => selectSingle(step.key, option)}
                />
              ))}
            </div>
          </fieldset>
        ) : null}
      </div>

      {step.kind !== 'contact' ? (
        <div className="mt-8 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={goBack}
            disabled={stepIndex === 0}
            className={stepIndex === 0 ? 'invisible' : ''}
          >
            Back
          </Button>
          <Button type="button" variant="primary" size="lg" onClick={goNext} disabled={!isAnswered()}>
            Next
          </Button>
        </div>
      ) : (
        <form
          id={FORM_ID}
          name={FORM_ID}
          data-netlify="true"
          data-readdy-form={FORM_ID}
          method="post"
          onSubmit={onSubmit}
          className="mt-7 space-y-5"
        >
          <input type="hidden" name="form-name" value={FORM_ID} />
          <input type="hidden" name="submitted_at" defaultValue="" />
          {categories.map((category) => (
            <input key={category} type="hidden" name="supplier_categories" value={category} />
          ))}
          {otherSelected ? (
            <input type="hidden" name="supplier_category_other" value={otherText} />
          ) : null}
          {Object.entries(answers).map(([key, value]) => (
            <input key={key} type="hidden" name={key} value={value} />
          ))}

          <p className="text-base leading-relaxed text-foreground-700">
            Almost done. Your answers are valuable on their own — sharing contact details is entirely
            optional and only needed if you would like to hear from us.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field
              label="First name (optional)"
              name="first_name"
              autoComplete="given-name"
              placeholder="Your first name"
            />
            <Field
              label="Last name (optional)"
              name="last_name"
              autoComplete="family-name"
              placeholder="Your last name"
            />
            <Field
              label="Business name (optional)"
              name="business_name"
              autoComplete="organization"
              placeholder="Your business name"
              className="sm:col-span-2"
            />
            <Field
              label={marketingConsent ? 'Email address' : 'Email address (optional)'}
              name="email"
              type="email"
              required={marketingConsent}
              autoComplete="email"
              placeholder="you@example.com"
              className="sm:col-span-2"
              hint={
                marketingConsent
                  ? 'Required because you have opted in to emails.'
                  : 'Optional — only needed if you would like to hear from us.'
              }
            />
          </div>

          <div className="space-y-4 pt-1">
            <CheckboxField
              name="marketing_consent"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
              label={
                <>
                  Yes, I&rsquo;d like to receive occasional emails from KOR38 about supplier
                  opportunities, business updates and joining the supplier network. I understand I can
                  unsubscribe at any time.{' '}
                  <span className="text-foreground-600">(Optional)</span>
                </>
              }
            />
          </div>

          <p className="text-sm leading-relaxed text-foreground-600">
            Your responses help us understand what matters to event suppliers and improve KOR38&rsquo;s service. See our{' '}
            <Link to="/privacy-policy" className="text-primary-300 underline underline-offset-2">
              Privacy Policy
            </Link>{'. '}You are welcome to take part for research only, without opting in to marketing.
          </p>

          <Honeypot />

          <div className="space-y-4">
            <FormStatus
              status={status}
              successMessage="Thank you for sharing your insights."
              errorMessage={errorMessage}
            />
            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
              <Button type="button" variant="ghost" onClick={goBack}>
                Back
              </Button>
              <Button type="submit" variant="primary" size="lg" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit my responses'}
              </Button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}